import React, { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  GET_CHECK_DETAILS_BY_TYPE,
  TRIGGER_CHECK_RUN,
  CREATE_COMMON_EXCEPTION,
  GET_COMMON_EXCEPTIONS,
} from "../../apis/settingsApis/framework-apis/frameworkApis";
import {
  CREATE_GLOBAL_PEOPLE_CHECK_EXCEPTION,
  GET_PEOPLE_CHECK_EXCEPTIONS,
  DELETE_PEOPLE_CHECK_EXCEPTION,
} from "../../apis/organization-apis/people-apis/peopleApis";
import {
  CREATE_AWS_CHECK_EXCEPTION,
  GET_AWS_CHECK_EXCEPTIONS,
  CREATE_AZURE_CHECK_EXCEPTION,
  GET_AZURE_CHECK_EXCEPTIONS,
  CREATE_GCP_CHECK_EXCEPTION,
  GET_GCP_CHECK_EXCEPTIONS,
  DELETE_AWS_CHECK_EXCEPTION,
  DELETE_AZURE_CHECK_EXCEPTION,
  DELETE_GCP_CHECK_EXCEPTION,
} from "../../apis/organization-apis/Infra-apis/InfraApis";
import { DELETE_COMMON_EXCEPTION } from "../../apis/settingsApis/framework-apis/frameworkApis";
import { CheckCategory, CheckStatus, ExceptionScope } from "../../utils/enums";
import { CheckStatusChip } from "../../components/CheckStatusChip/CheckStatusChip";
import ConfirmSnackbar from "../../components/Snackbar/ConfirmSnackbar";
import ResourcesTab from "./components/ResourcesTab";
import { ExceptionDetailsForm } from "../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/ExceptionDetailsForm";
import EvidenceUpload from "./components/EvidenceUpload/EvidenceUpload";
import AICheckEvidenceSuggestions from "./components/AICheckEvidenceSuggestions";
import { useToday, formatDate } from "../../utils/dateUtils";
import CheckNotRunAlert from "./components/common/CheckNotRunAlert";
import { useCheckRunStatusPolling } from "./hooks/useCheckRunStatusPolling";
import DeactivatedIntegrationsAlert from "./components/common/DeactivatedIntegrationsAlert";
import RemediationStepsPanel from "./components/common/RemediationStepsPanel";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../components/AccessControl";

const CheckDetailsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { checkType } = useParams();
  const today = useToday();
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [globalExceptionOpen, setGlobalExceptionOpen] = useState(false);
  const [formData, setFormData] = useState({ reason: "", validUntil: "" });
  const [submitLoading, setSubmitLoading] = useState(false);
  const resourcesRefetchRef = React.useRef(null);

  const {
    data,
    loading: checkLoading,
    error: checkError,
    refetch: refetchCheckDetails,
  } = useQuery(GET_CHECK_DETAILS_BY_TYPE, {
    variables: { check_type: checkType },
    skip: !checkType,
    fetchPolicy: "network-only",
  });

  const checkDetails = data?.getCheckDetailsByType;
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;

  const handlePollingComplete = useCallback(async () => {
    await refetchCheckDetails();
    if (resourcesRefetchRef.current) {
      await resourcesRefetchRef.current();
    }
  }, [refetchCheckDetails]);

  const { isPollingRunStatus, startPolling } = useCheckRunStatusPolling(
    activeCheckId,
    handlePollingComplete
  );

  const [triggerRun, { loading: triggerLoading }] =
    useMutation(TRIGGER_CHECK_RUN);

  const checkCategoryLower =
    checkDetails?.active_check_category?.toLowerCase() || "";
  const isPeopleCheck = checkCategoryLower.includes(
    CheckCategory?.PEOPLE_CHECK
  );
  const isAwsCheck = checkCategoryLower.includes(
    CheckCategory?.AWS_SYSTEM_CHECK
  );
  const isAzureCheck = checkCategoryLower.includes(
    CheckCategory?.AZURE_SYSTEM_CHECK
  );
  const isGcpCheck = checkCategoryLower.includes(
    CheckCategory?.GCP_SYSTEM_CHECK
  );

  const isPassWithEvidence = checkCategoryLower.includes(
    CheckCategory?.PASS_WITH_EVIDENCE
  );

  const [createGlobalExceptionPeople] = useMutation(
    CREATE_GLOBAL_PEOPLE_CHECK_EXCEPTION,
    { skip: !isPeopleCheck }
  );
  const [createAwsException] = useMutation(CREATE_AWS_CHECK_EXCEPTION, {
    skip: !isAwsCheck,
  });
  const [createAzureException] = useMutation(CREATE_AZURE_CHECK_EXCEPTION, {
    skip: !isAzureCheck,
  });
  const [createGcpException] = useMutation(CREATE_GCP_CHECK_EXCEPTION, {
    skip: !isGcpCheck,
  });
  const [createCommonException] = useMutation(CREATE_COMMON_EXCEPTION, {
    skip: isPeopleCheck || isAwsCheck || isAzureCheck || isGcpCheck,
  });

  const [deletePeopleException] = useMutation(DELETE_PEOPLE_CHECK_EXCEPTION, {
    skip: !isPeopleCheck,
  });
  const [deleteAwsException] = useMutation(DELETE_AWS_CHECK_EXCEPTION, {
    skip: !isAwsCheck,
  });
  const [deleteAzureException] = useMutation(DELETE_AZURE_CHECK_EXCEPTION, {
    skip: !isAzureCheck,
  });
  const [deleteGcpException] = useMutation(DELETE_GCP_CHECK_EXCEPTION, {
    skip: !isGcpCheck,
  });
  const [deleteCommonException] = useMutation(DELETE_COMMON_EXCEPTION, {
    skip: isPeopleCheck || isAwsCheck || isAzureCheck || isGcpCheck,
  });

  const getExceptionQueryConfig = useCallback(
    (checkId) => {
      if (isPeopleCheck) {
        return {
          query: GET_PEOPLE_CHECK_EXCEPTIONS,
          variables: {
            filter: { active_check_id: Number(checkId) },
            page: 1,
            limit: 100,
          },
        };
      } else if (isAwsCheck) {
        return {
          query: GET_AWS_CHECK_EXCEPTIONS,
          variables: {
            filter: { active_check_id: Number(checkId) },
            page: 1,
            limit: 100,
          },
        };
      } else if (isAzureCheck) {
        return {
          query: GET_AZURE_CHECK_EXCEPTIONS,
          variables: {
            filter: { active_check_id: Number(checkId) },
            page: 1,
            limit: 100,
          },
        };
      } else if (isGcpCheck) {
        return {
          query: GET_GCP_CHECK_EXCEPTIONS,
          variables: {
            filter: { active_check_id: Number(checkId) },
            page: 1,
            limit: 100,
          },
        };
      } else {
        return {
          query: GET_COMMON_EXCEPTIONS,
          variables: {
            filters: { active_check_id: Number(checkId) },
            page: 1,
            limit: 100,
          },
        };
      }
    },
    [isPeopleCheck, isAwsCheck, isAzureCheck, isGcpCheck]
  );

  const extractExceptions = useCallback(
    (exceptionsData) => {
      if (!exceptionsData) return [];
      if (isPeopleCheck)
        return exceptionsData?.getPeopleCheckExceptions?.data || [];
      if (isAwsCheck) return exceptionsData?.getAwsCheckExceptions?.data || [];
      if (isAzureCheck)
        return exceptionsData?.getAzureCheckExceptions?.data || [];
      if (isGcpCheck) return exceptionsData?.getGcpCheckExceptions?.data || [];
      return exceptionsData?.getCommonExceptions?.data || [];
    },
    [isPeopleCheck, isAwsCheck, isAzureCheck, isGcpCheck]
  );

  const exceptionQueryConfig = useMemo(
    () => getExceptionQueryConfig(activeCheckId),
    [getExceptionQueryConfig, activeCheckId]
  );

  const { data: exceptionsData, refetch: refetchExceptionsQuery } = useQuery(
    exceptionQueryConfig.query,
    {
      variables: exceptionQueryConfig.variables,
      skip: !activeCheckId,
    }
  );

  const globalException = useMemo(() => {
    const exceptions = extractExceptions(exceptionsData);
    return exceptions.find((exc) => {
      if (
        exc?.scope !== ExceptionScope?.GLOBAL ||
        exc?.is_active !== true
      ) {
        return false;
      }
      if (isPassWithEvidence) {
        const isEvidenceLinked =
          exc?.entity_type === "file" ||
          exc?.file_id != null ||
          (exc?.entity_id != null &&
            exc?.entity_id !== "" &&
            !Number.isFinite(Number(exc?.entity_id)));
        if (isEvidenceLinked) return false;
      }
      return (
        exc?.entity_id == null ||
        exc?.entity_id === "" ||
        !Number.isFinite(Number(exc?.entity_id))
      );
    });
  }, [exceptionsData, extractExceptions, isPassWithEvidence]);

  const passWithEvidenceException = useMemo(() => {
    const exceptions = extractExceptions(exceptionsData);
    return exceptions.find((exc) => {
      if (exc?.is_active !== true) return false;
      if (exc?.scope === ExceptionScope?.PASS_WITH_EVIDENCE_GLOBAL) return true;
      if (exc?.scope !== ExceptionScope?.GLOBAL) return false;
      if (
        exc?.entity_id != null &&
        exc?.entity_id !== "" &&
        Number.isFinite(Number(exc?.entity_id))
      ) {
        return true;
      }
      if (!isPassWithEvidence) return false;
      return (
        exc?.entity_type === "file" ||
        exc?.file_id != null ||
        (exc?.entity_id != null &&
          exc?.entity_id !== "" &&
          !Number.isFinite(Number(exc?.entity_id)))
      );
    });
  }, [exceptionsData, extractExceptions, isPassWithEvidence]);

  const checkCategory = React.useMemo(() => {
    if (!checkCategoryLower) return null;
    if (checkCategoryLower.includes(CheckCategory?.AZURE_SYSTEM_CHECK))
      return CheckCategory?.AZURE_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.AWS_SYSTEM_CHECK))
      return CheckCategory?.AWS_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.GCP_SYSTEM_CHECK))
      return CheckCategory?.GCP_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.PEOPLE_CHECK))
      return CheckCategory?.PEOPLE_CHECK;
    return null;
  }, [checkCategoryLower]);

  const getRemediationSteps = React.useCallback((steps) => {
    const empty = {
      uiSteps: [],
      programmaticSteps: null,
      remediationEnabled: false,
    };
    if (!steps) return empty;

    let parsed = steps;

    // Handle if it's a JSON string
    if (typeof steps === "string") {
      try {
        parsed = JSON.parse(steps);
      } catch {
        return empty;
      }
    }

    if (typeof parsed === "object") {
      const uiSteps =
        parsed?.ui_remediation_steps?.steps ||
        parsed?.steps ||
        (Array.isArray(parsed) ? parsed : []);
      const programmaticSteps = parsed?.programmatic_remediation_steps || null;

      return {
        uiSteps: Array.isArray(uiSteps) ? uiSteps : [],
        programmaticSteps: programmaticSteps?.config?.command
          ? programmaticSteps
          : null,
        // Opt-in: the Remediate button appears only for checks whose recipe has
        // been verified end to end and explicitly marked. Anything else --
        // absent, false, or a truthy non-true value -- keeps it hidden, because
        // an unverified recipe prefills values that look authoritative and are
        // wrong. Mirrored server-side in infra.service.js.
        remediationEnabled:
          programmaticSteps?.config?.remediation_enabled === true,
      };
    }

    return empty;
  }, []);

  const remediationData = React.useMemo(() => {
    return getRemediationSteps(checkDetails?.remediation_steps);
  }, [checkDetails?.remediation_steps, getRemediationSteps]);

  const [descriptionExpanded, setDescriptionExpanded] = React.useState(false);

  const isTriggerDisabled = React.useMemo(() => {
    const cat = checkDetails?.active_check_category;
    const type = checkDetails?.active_check_type;
    return (
      cat?.includes(CheckCategory.PASS_WITH_EVIDENCE) ||
      (cat?.includes(CheckCategory.POLICY_CHECK) &&
        !type?.includes("accepted_by_employees")) ||
      cat?.includes(CheckCategory.POLICY_CONTENT_CHECK)
    );
  }, [checkDetails]);


  const handleTriggerRun = async () => {
    if (!activeCheckId) {
      setSnackbarInfo({
        message: "Check ID not available",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }
    try {
      const result = await triggerRun({
        variables: { check_id: Number(activeCheckId) },
      });
      if (result.data?.triggerCheckRun?.success) {
        setSnackbarInfo({
          message: "Check run triggered successfully!",
          vertical: "top",
          horizontal: "right",
        });
        startPolling();
        await refetchCheckDetails();
      } else {
        setSnackbarInfo({
          message:
            result.data?.triggerCheckRun?.message ||
            "Failed to trigger check run",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to trigger check run",
        vertical: "top",
        horizontal: "right",
      });
    }
  };


  const isValidUntilPast = formData.validUntil && formData.validUntil < today;

  const isValid = useMemo(() => {
    return (
      formData.reason?.trim()?.length > 0 &&
      formData.validUntil &&
      formData.validUntil >= today
    );
  }, [formData.reason, formData.validUntil, today]);

  const resetForm = useCallback(() => {
    setFormData({ reason: "", validUntil: "" });
  }, []);

  const handleOpenGlobalException = () => {
    resetForm();
    setGlobalExceptionOpen(true);
  };

  const getDeleteMutation = useCallback(() => {
    if (isPeopleCheck) return deletePeopleException;
    if (isAwsCheck) return deleteAwsException;
    if (isAzureCheck) return deleteAzureException;
    if (isGcpCheck) return deleteGcpException;
    return deleteCommonException;
  }, [
    isPeopleCheck,
    isAwsCheck,
    isAzureCheck,
    isGcpCheck,
    deletePeopleException,
    deleteAwsException,
    deleteAzureException,
    deleteGcpException,
    deleteCommonException,
  ]);

  const getCreateMutation = useCallback(() => {
    if (isPeopleCheck) return createGlobalExceptionPeople;
    if (isAwsCheck) return createAwsException;
    if (isAzureCheck) return createAzureException;
    if (isGcpCheck) return createGcpException;
    return createCommonException;
  }, [
    isPeopleCheck,
    isAwsCheck,
    isAzureCheck,
    isGcpCheck,
    createGlobalExceptionPeople,
    createAwsException,
    createAzureException,
    createGcpException,
    createCommonException,
  ]);

  const handleDeleteGlobalException = useCallback(async () => {
    if (!globalException?.id) return;
    setSubmitLoading(true);
    try {
      const deleteMutation = getDeleteMutation();
      await deleteMutation({
        variables: { ids: [Number(globalException.id)] },
      });

      setSnackbarInfo({
        message: "Check enabled successfully!",
        vertical: "top",
        horizontal: "right",
      });
      await refetchExceptionsQuery();
      await refetchCheckDetails();
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to enable check.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setSubmitLoading(false);
      startPolling()
    }
  }, [
    globalException,
    getDeleteMutation,
    refetchExceptionsQuery,
    refetchCheckDetails,
  ]);

  const handleCloseDialog = () => {
    if (!submitLoading) {
      setGlobalExceptionOpen(false);
      resetForm();
    }
  };

  const handleApplyException = useCallback(async () => {
    if (!isValid) return;
    setSubmitLoading(true);
    try {
      const createMutation = getCreateMutation();
      const isCommonException =
        !isPeopleCheck && !isAwsCheck && !isAzureCheck && !isGcpCheck;

      const variables = isCommonException
        ? {
          active_check_id: Number(activeCheckId),
          reason: formData.reason,
          scope: ExceptionScope?.GLOBAL,
          exception_valid_till: formData.validUntil || "",
          entity_id: null,
          entity_type: null,
        }
        : {
          exception_data: {
            active_check_id: Number(activeCheckId),
            reason: formData.reason,
            scope: ExceptionScope?.GLOBAL,
            exception_valid_till: formData.validUntil || "",
          },
        };

      await createMutation({ variables });

      resetForm();
      setSnackbarInfo({
        message: "Exception created successfully!",
        vertical: "top",
        horizontal: "right",
      });
      await refetchExceptionsQuery();
      await refetchCheckDetails();
      setGlobalExceptionOpen(false);
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to create exception.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setSubmitLoading(false);
      startPolling()
    }
  }, [
    isValid,
    activeCheckId,
    formData,
    isPeopleCheck,
    isAwsCheck,
    isAzureCheck,
    isGcpCheck,
    getCreateMutation,
    resetForm,
    refetchExceptionsQuery,
    refetchCheckDetails,
  ]);

  if (checkLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!checkType || checkError || !checkDetails) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error">
          Error loading check
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {checkError?.message || "Check not found"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.customColors.background.header,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <DeactivatedIntegrationsAlert
        checkCategory={checkDetails?.active_check_category}
        onManageIntegrations={() => navigate("/integrations?domain=cloud_provider")}
      />

      {/* Header Section */}
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          backgroundColor: theme.customColors.background.main,
          boxShadow: theme.customColors.shadow.main,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Title and Actions Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  textTransform: "capitalize",
                  color: theme.customColors.text.strong,
                }}
              >
                {checkDetails?.active_check_name?.replace(/_/g, " ") ||
                  "Check Details"}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.customColors.text.secondary,
                  lineHeight: 1.6,
                  maxWidth: "900px",
                  fontSize: "0.875rem",
                }}
              >
                {(() => {
                  const description =
                    checkDetails?.active_check_description ||
                    "No description available";
                  const shouldTruncate = description.length > 200;
                  const displayText =
                    shouldTruncate && !descriptionExpanded
                      ? description.substring(0, 150) + "..."
                      : description;

                  return (
                    <>
                      {displayText}
                      {shouldTruncate && (
                        <Button
                          onClick={() =>
                            setDescriptionExpanded(!descriptionExpanded)
                          }
                          sx={{
                            textTransform: "none",
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            p: 0,
                            minWidth: "auto",
                            ml: 0.5,
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {descriptionExpanded ? "Show less" : "Show more"}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Tooltip
                title={
                  isTriggerDisabled
                    ? "This check doesn't support manual triggering"
                    : ""
                }
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleTriggerRun}
                    disabled={
                      isTriggerDisabled ||
                      triggerLoading ||
                      isPollingRunStatus ||
                      submitLoading
                    }
                    startIcon={
                      !(triggerLoading || isPollingRunStatus) && (
                        <PlayArrowIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    endIcon={
                      triggerLoading || isPollingRunStatus && (
                        <CircularProgress size={16} color="inherit" />
                      )
                    }
                    sx={{
                      bgcolor: "#3B82F6",
                      color: "#fff",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#2563EB",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&:disabled": {
                        backgroundColor: theme.customColors.background.disabled,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    {(triggerLoading || isPollingRunStatus)
                      ? "Refreshing Results"
                      : "Trigger Check Run"}
                  </Button>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  globalException
                    ? "Enable this check by removing the global exception"
                    : "Disable this check globally for all resources"
                }
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={
                      globalException
                        ? handleDeleteGlobalException
                        : handleOpenGlobalException
                    }
                    disabled={submitLoading}
                    sx={{
                      bgcolor: "#3B82F6",
                      color: "#fff",
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#2563EB",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&:disabled": {
                        bgcolor: theme.customColors.background.tableRow,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    {globalException ? "Enable Check" : "Disable Check"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Status Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              pt: 2.5,
              mt: 2.5,
              borderTop: `1px solid ${theme.customColors.background.tableRow}`,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.customColors.text.strong,
                fontSize: "0.875rem",
                letterSpacing: "0.01em",
                flexShrink: 0,
              }}
            >
              Status:
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
            <CheckStatusChip
            isRefreshing={isPollingRunStatus}
              status={
                globalException
                  ? "disabled"
                  : passWithEvidenceException
                    ? "pass"
                    : checkDetails?.status
              }
              count={0}
            />
            </Box>
            {globalException && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  ml: 0.5,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {globalException?.reason && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        flexShrink: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      •
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        minWidth: 0,
                      }}
                    >
                      {globalException.reason}
                    </Typography>
                  </Box>
                )}
                {globalException?.exception_valid_till && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                      }}
                    >
                      •
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Until {formatDate(globalException.exception_valid_till)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {passWithEvidenceException && !globalException && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  ml: 0.5,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {passWithEvidenceException?.reason && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        flexShrink: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      •
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        minWidth: 0,
                      }}
                    >
                      {passWithEvidenceException.reason}
                    </Typography>
                  </Box>
                )}
                {passWithEvidenceException?.exception_valid_till && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                      }}
                    >
                      •
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        fontSize: "0.8125rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Until{" "}
                      {formatDate(
                        passWithEvidenceException.exception_valid_till
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {!globalException &&
              !passWithEvidenceException &&
              (!checkDetails?.employee_results ||
                checkDetails.employee_results.length === 0) &&
              checkDetails?.status_reason &&
              (!checkDetails?.resources ||
                checkDetails.resources.length === 0) &&
              !isAwsCheck &&
              !isAzureCheck &&
              !isGcpCheck && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    ml: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.customColors.text.secondary,
                      fontSize: "0.8125rem",
                    }}
                  >
                    •
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.customColors.text.secondary,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {checkDetails.status_reason}
                  </Typography>
                </Box>
              )}
          </Box>

          <RemediationStepsPanel
            remediationData={remediationData}
            isAwsCheck={isAwsCheck}
            isAzureCheck={isAzureCheck}
            isGcpCheck={isGcpCheck}
          />
                      </Box>

      </Box>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!isTriggerDisabled &&
          checkDetails?.status === CheckStatus.PENDING &&
          (isPeopleCheck || (!isAwsCheck && !isAzureCheck && !isGcpCheck)) &&
          ((isPeopleCheck && (!checkDetails?.employee_results || checkDetails.employee_results.length === 0)) ||
            (!isPeopleCheck && (!checkDetails?.resources || checkDetails.resources.length === 0))) && (
            <CheckNotRunAlert />
          )}
        <ResourcesTab
          checkDetails={checkDetails}
          activeCheckId={activeCheckId}
          checkCategory={checkCategory}
          onRefetch={refetchCheckDetails}
          onStartPolling={startPolling}
          globalException={globalException}
          isTriggerDisabled={isTriggerDisabled}
          checkStatus={checkDetails?.status}
          remediationEnabled={remediationData.remediationEnabled}
          isPollingRunStatus={isPollingRunStatus}
          onRegisterRefetch={(refetchFn) => {
            resourcesRefetchRef.current = refetchFn;
          }}
        />

        {aiEvidenceEnabled && (
          <Box sx={{ mt: 3 }}>
            <AICheckEvidenceSuggestions activeCheckId={activeCheckId} />
          </Box>
        )} 

        {/* Evidence Upload Section */}
        <Box sx={{ mt: 3 }}>
          <EvidenceUpload
            activeCheckId={activeCheckId}
            onSubmit={refetchCheckDetails}
            isPassWithEvidence={isPassWithEvidence}
            onRefetchCheckDetails={refetchCheckDetails}
          />
        </Box>
      </Box>

      {/* Global Exception Dialog */}
      <Dialog
        open={globalExceptionOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 600,
            maxWidth: 700,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2,
            px: 3,
          }}
        >
          Disable Check
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 0,
            "&.MuiDialogContent-root": {
              paddingTop: 3,
            },
          }}
        >
          <ExceptionDetailsForm
            reason={formData.reason}
            setReason={(val) => setFormData({ ...formData, reason: val })}
            validUntil={formData.validUntil}
            setValidUntil={(val) =>
              setFormData({ ...formData, validUntil: val })
            }
            isValidUntilPast={isValidUntilPast}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
            pt: 2,
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            mt: 1,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            disabled={submitLoading}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyException}
            variant="contained"
            disabled={!isValid || submitLoading}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: theme.customColors.button.primary,
              color: theme.customColors.button.text,
              "&:hover": {
                bgcolor: theme.palette._blue.dark,
              },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            {submitLoading ? "Disabling..." : "Disable Check"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default CheckDetailsPage;
